/**
 * 远程监控配置服务
 *
 * 从后端获取监控开关状态，支持紧急关闭监控功能。
 * 在初始化监控 SDK 前调用，根据远程配置决定是否启用监控。
 *
 * @author YSFL Team
 * @date 2026-01-28
 */

import { resolveApiUrl } from '#/api/runtime-config';

/**
 * Web 端监控配置类型
 *
 * 使用 ?platform=web 参数时返回扁平结构，更精简
 */
export interface MonitoringRemoteConfig {
  version: number; // 配置版本号（时间戳）
  globalEnabled: boolean;
  // 扁平结构（?platform=web 时返回）
  errorEnabled?: boolean;
  performanceEnabled?: boolean;
  behaviorEnabled?: boolean; // 用户行为追踪开关
  sampleRate?: number;
  /** 日志服务 URL（空字符串表示使用后端 API，可切换到独立日志服务器） */
  logServiceUrl?: string;
  // 嵌套结构（兼容旧版本，无 platform 参数时返回）
  web?: {
    behaviorEnabled?: boolean;
    errorEnabled: boolean;
    logServiceUrl?: string;
    performanceEnabled: boolean;
    sampleRate: number;
  };
  app?: {
    errorEnabled: boolean;
    journeyEnabled: boolean;
    performanceEnabled: boolean;
    sampleRate: number;
  };
}

export interface WebMonitoringConfig {
  behaviorEnabled: boolean;
  errorEnabled: boolean;
  logServiceUrl?: string;
  performanceEnabled: boolean;
  sampleRate: number;
}

/** 默认配置（远程获取失败时使用） */
const DEFAULT_CONFIG: MonitoringRemoteConfig = {
  version: 0,
  globalEnabled: true,
  errorEnabled: true,
  performanceEnabled: true,
  behaviorEnabled: true, // 默认启用行为追踪
  sampleRate: 0.1,
};

/** 缓存的配置 */
let cachedConfig: MonitoringRemoteConfig | null = null;

/** 配置缓存时间（5分钟） */
const CACHE_TTL = 5 * 60 * 1000;

/** 上次获取时间 */
let lastFetchTime = 0;

/**
 * 获取远程监控配置
 *
 * 会缓存配置，避免频繁请求。
 * 如果获取失败，返回默认配置（全部启用）。
 */
export async function getMonitoringConfig(): Promise<MonitoringRemoteConfig> {
  // 检查缓存是否有效
  if (cachedConfig && Date.now() - lastFetchTime < CACHE_TTL) {
    return cachedConfig;
  }

  try {
    // 同源部署使用相对路径，绝对 API 配置则由浏览器直连。
    // 添加 platform=web 参数，只获取 Web 配置，减少响应体
    const response = await fetch(
      resolveApiUrl('/api/v1/monitoring/config?platform=web'),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();

    if (result.code === 0 && result.data) {
      const nextConfig = result.data as MonitoringRemoteConfig;
      cachedConfig = nextConfig;
      lastFetchTime = Date.now();
      return nextConfig;
    }

    throw new Error(result.message || 'Invalid response');
  } catch (error) {
    // 获取失败时使用默认配置
    if (import.meta.env.DEV) {
      console.warn('[MonitoringConfig] 获取远程配置失败，使用默认配置:', error);
    }
    return DEFAULT_CONFIG;
  }
}

/**
 * 强制刷新配置缓存
 */
export function refreshMonitoringConfig(): void {
  cachedConfig = null;
  lastFetchTime = 0;
}

/**
 * 获取 Web 监控配置（便捷方法）
 *
 * 支持两种响应结构：
 * - 扁平结构（?platform=web）：直接从 config 读取
 * - 嵌套结构（无 platform 参数）：从 config.web 读取
 */
export async function getWebMonitoringConfig(): Promise<WebMonitoringConfig> {
  const config = await getMonitoringConfig();

  if (!config.globalEnabled) {
    return {
      behaviorEnabled: false,
      errorEnabled: false,
      logServiceUrl: undefined,
      performanceEnabled: false,
      sampleRate: 0,
    };
  }

  // 支持扁平结构（?platform=web 返回）
  if (config.errorEnabled !== undefined) {
    return {
      behaviorEnabled: config.behaviorEnabled ?? true,
      errorEnabled: config.errorEnabled,
      logServiceUrl: config.logServiceUrl,
      performanceEnabled: config.performanceEnabled ?? true,
      sampleRate: config.sampleRate ?? 0.1,
    };
  }

  // 兼容嵌套结构（旧版本）
  if (config.web) {
    return {
      behaviorEnabled: config.web.behaviorEnabled ?? true,
      errorEnabled: config.web.errorEnabled,
      logServiceUrl: config.web.logServiceUrl,
      performanceEnabled: config.web.performanceEnabled,
      sampleRate: config.web.sampleRate,
    };
  }

  // 兜底默认值
  return {
    behaviorEnabled: true,
    errorEnabled: true,
    logServiceUrl: undefined,
    performanceEnabled: true,
    sampleRate: 0.1,
  };
}

/**
 * 从上传响应中更新配置
 *
 * 当日志上传响应中包含 monitoringConfig 时调用此函数。
 * 支持两种响应模式：
 * 1. 完整模式：响应包含完整配置（有 globalEnabled 字段），直接更新本地缓存
 * 2. 精简模式：响应只包含版本号（只有 version 字段），比较版本后主动拉取完整配置
 *
 * @param responseConfig 上传响应中的配置（完整配置或仅版本号）
 * @returns 是否有更新
 */
export async function updateConfigFromResponse(
  responseConfig: null | Partial<MonitoringRemoteConfig> | undefined,
): Promise<boolean> {
  if (!responseConfig || responseConfig.version === undefined) {
    return false;
  }

  // 比较版本号
  const currentVersion = cachedConfig?.version ?? 0;
  const newVersion = responseConfig.version ?? 0;

  if (newVersion > currentVersion) {
    // 判断是完整配置还是仅版本号
    if (
      'globalEnabled' in responseConfig &&
      responseConfig.globalEnabled !== undefined
    ) {
      // 完整模式：直接使用响应中的配置
      cachedConfig = responseConfig as MonitoringRemoteConfig;
      lastFetchTime = Date.now();

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.info(
          '[MonitoringConfig] 配置已从上传响应中更新（完整模式）:',
          `v${currentVersion} → v${newVersion}`,
        );
      }
    } else {
      // 精简模式：只有版本号，需要主动拉取完整配置
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.info(
          '[MonitoringConfig] 检测到配置更新（精简模式），正在拉取完整配置...',
          `v${currentVersion} → v${newVersion}`,
        );
      }

      // 清除缓存并重新获取
      cachedConfig = null;
      lastFetchTime = 0;
      await getMonitoringConfig();
    }

    return true;
  }

  return false;
}

/**
 * 获取当前配置版本号
 */
export function getCurrentConfigVersion(): number {
  return cachedConfig?.version ?? 0;
}
